import { useState, useMemo, useContext, memo, useLayoutEffect } from "react";
import LazyLoad from "react-lazy-load";
import CollectionButton from "./CollectionButton";
import ToolTip from "./ToolTip";
import { MaterialContext } from "../context/MaterialContext";
import { UserContext } from "../context/UserContext";
import { adidasLogo, nikeLogo } from "./Icons";
import {
  getBrandDataForSlug,
  getRowCustomerBrand,
} from "../utilities/brandData";
import { getSessionCustomerBrand } from "../utilities/loginAccount";

import swatchStyle from "../styles/swatch.module.scss";

import MaterialModal from "./MaterialModal";

const loadedSwatchUrls = new Set();

function BrandMark({ logo, label, brandData, media, onOpenMore }) {
  const [open, setOpen] = useState(false);

  if (!Array.isArray(brandData) || brandData.length === 0) {
    return null;
  }

  return (
    <div
      className={`${swatchStyle.brandMarkWrap} ${
        open ? swatchStyle.brandMarkOpen : ""
      }`}
      onMouseEnter={() => {
        if (media !== "mobile") {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (media !== "mobile") {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={swatchStyle.brandMark}
        aria-label={label}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          if (media === "mobile") {
            setOpen((isOpen) => !isOpen);
          }
        }}
      >
        {logo}
      </button>
      <ToolTip
        brandData={brandData}
        showToolTip={open}
        onOpenMore={() => {
          setOpen(false);
          onOpenMore();
        }}
      />
    </div>
  );
}

const Swatch = memo(function Swatch({ id }) {
  const { materialById, nikeData, changeSearch, ROOT_SERVER } =
    useContext(MaterialContext);
  const { userInformation, media } = useContext(UserContext);

  const [imageStatus, setImageStatus] = useState("loading");
  const [imageNumber, setImageNumber] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const sessionBrand = getSessionCustomerBrand(userInformation);
  const vendor = String(userInformation?.vendor || "")
    .trim()
    .toLowerCase();
  const showLogo =
    vendor === "asiatan" || vendor === "nike" || vendor === "adidas";

  const showCount = vendor === "asiatan" || vendor === "tectuff";

  const materialData = useMemo(() => {
    if (!id) {
      return null;
    }

    return materialById?.get(id) ?? null;
  }, [id, materialById]);

  const brandData = useMemo(() => {
    if (!materialData?.slug) {
      return [];
    }

    return getBrandDataForSlug(nikeData, materialData.slug, sessionBrand);
  }, [materialData?.slug, nikeData, sessionBrand]);

  const nikeBrandRows = useMemo(
    () =>
      brandData.filter((row) => getRowCustomerBrand(row) !== "adidas"),
    [brandData],
  );
  const adidasBrandRows = useMemo(
    () =>
      brandData.filter((row) => getRowCustomerBrand(row) === "adidas"),
    [brandData],
  );

  const hasBrandMarks =
    showLogo && (nikeBrandRows.length > 0 || adidasBrandRows.length > 0);

  const viewing = useMemo(() => {
    if (!materialData) {
      return "";
    }

    const materialImages = Array.isArray(materialData.images)
      ? materialData.images
      : [];
    const safeImage = materialImages[imageNumber] || materialImages[0] || "";

    return safeImage
      ? `${ROOT_SERVER}${materialData.vendor}/${materialData.slug}/swatch/${safeImage}`
      : "";
  }, [materialData, imageNumber, ROOT_SERVER]);

  useLayoutEffect(() => {
    if (!viewing) {
      setImageStatus("empty");
      return;
    }

    if (loadedSwatchUrls.has(viewing)) {
      setImageStatus("loaded");
      return;
    }

    setImageStatus("loading");
  }, [viewing]);

  const handleChangeImage = (i) => {
    setImageNumber(i);
  };

  const handleImageLoaded = () => {
    if (viewing) {
      loadedSwatchUrls.add(viewing);
    }
    setImageStatus("loaded");
  };

  const handleImageError = () => {
    setImageStatus("error");
  };

  const showImageFallback = imageStatus === "empty" || imageStatus === "error";
  const handleShowModal = () => {
    setShowModal(true);
  };

  if (!materialData) {
    return null;
  }

  const { title, description, vendor: materialVendor, slug, images, view_count } =
    materialData;

  return (
    <>
      {showModal && (
        <MaterialModal
          id={id}
          showModal={showModal}
          setShowModal={setShowModal}
          brandData={brandData}
          userInformation={userInformation}
          media={media}
          imageNum={imageNumber}
        />
      )}
      <div className={swatchStyle.swatch} data-id={id}>
        <div className={swatchStyle.swatchImageFrame}>
          {showImageFallback ? (
            <button
              type="button"
              className={swatchStyle.swatchImageFallback}
              onClick={handleShowModal}
              aria-label={`Open ${title || slug}`}
            >
              <span className={swatchStyle.swatchImageFallbackLabel}>
                {imageStatus === "error" ? "Image unavailable" : "No image"}
              </span>
              <span className={swatchStyle.swatchImageFallbackTitle}>
                {title || slug.replaceAll("-", " ")}
              </span>
            </button>
          ) : (
            <LazyLoad height="100%" width="100%" offset={1000} threshold={0}>
              <img
                onClick={handleShowModal}
                className={
                  imageStatus === "loaded"
                    ? `${swatchStyle.imageBlur} ${swatchStyle.loaded}`
                    : `${swatchStyle.imageBlur}`
                }
                src={viewing}
                alt={slug}
                onLoad={handleImageLoaded}
                onError={handleImageError}
                ref={(image) => {
                  if (
                    image?.complete &&
                    image.naturalWidth > 0 &&
                    image.getAttribute("src") === viewing
                  ) {
                    handleImageLoaded();
                  }
                }}
              />
            </LazyLoad>
          )}
          {images?.length >= 2 && (
            <div className={swatchStyle.imageSelector}>
              {images?.map((image, i) => (
                <span
                  key={image}
                  onClick={() => handleChangeImage(i)}
                  style={
                    imageNumber === i
                      ? { background: "transparent" }
                      : { background: "#FEFEFE90" }
                  }
                />
              ))}
            </div>
          )}
        </div>
        <div className={swatchStyle.vendor}>
          <p
            className={swatchStyle.vendorLabel}
            onClick={() => changeSearch(materialVendor)}
          >
            {materialVendor}
          </p>
          {hasBrandMarks ? (
            <div className={swatchStyle.brandMarks}>
              <BrandMark
                logo={nikeLogo}
                label="Nike brand info"
                brandData={nikeBrandRows}
                media={media}
                onOpenMore={handleShowModal}
              />
              <BrandMark
                logo={adidasLogo}
                label="Adidas brand info"
                brandData={adidasBrandRows}
                media={media}
                onOpenMore={handleShowModal}
              />
            </div>
          ) : null}
          {showCount ? <p>views: {view_count}</p> : null}
        </div>
        <div className={swatchStyle.collectionButton}>
          <CollectionButton materialId={id} />
        </div>

        <div className={swatchStyle.information}>
          {imageStatus === "loading" && viewing ? (
            <div className={swatchStyle.loadingBar} />
          ) : null}
          <h3>{title}</h3>
          <h4>{description}</h4>
        </div>
      </div>
    </>
  );
});

export default Swatch;
